import React, { useEffect, useMemo, useState } from "react";
import Widget from "../components/Widget.jsx";
import "../styles/App.css";
import { listenDashboardSettingsHybrid } from "../services/data";

export default function Dashboard({
  user,
  onGoUpload,
  onGoEdit,
  setRoute,
  onEditRecord,
}) {
  const [allWidgets, setAllWidgets] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});

  // Reload dashboard configuration when the user changes
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = listenDashboardSettingsHybrid(user.uid, (data) => {
      const ws = Array.isArray(data?.widgets) ? data.widgets : [];
      setAllWidgets(ws);
      // Initialiser l'état (par défaut replié) pour les widgets table
      const initialExpanded = {};
      (ws || []).filter(w => !w?.layout?.hidden).forEach(widget => {
        if (widget.type === "table_list" || widget.type === "relation_table" || widget.type === "recent_records") {
          initialExpanded[widget.id] = false;
        }
      });
      setExpandedSections(initialExpanded);
    });
    return () => {
      try {
        unsub && unsub();
      } catch {
        /* noop */
      }
    };
  }, [user?.uid]);

  const visibleWidgets = useMemo(
    () => allWidgets.filter((w) => !w?.layout?.hidden),
    [allWidgets]
  );

  const kpiWidgets = useMemo(() => {
    return visibleWidgets.filter((w) => {
      if (w.type === "kpi_count" || w.type === "relations_kpi") return true;
      if (w.type === "aggregate") {
        const display = w.props?.display || "metric";
        return display === "metric";
      }
      return false;
    });
  }, [visibleWidgets]);

  const otherWidgets = useMemo(
    () => visibleWidgets.filter((w) => !kpiWidgets.includes(w)),
    [visibleWidgets, kpiWidgets]
  );

  const tableWidgets = useMemo(
    () =>
      otherWidgets.filter(
        (w) =>
          w.type === "table_list" ||
          w.type === "relation_table" ||
          w.type === "recent_records"
      ),
    [otherWidgets]
  );

  const nonTableWidgets = useMemo(
    () =>
      otherWidgets.filter(
        (w) =>
          !(
            w.type === "table_list" ||
            w.type === "relation_table" ||
            w.type === "recent_records"
          )
      ),
    [otherWidgets]
  );

  // Keep the entry animation from the previous version
  useEffect(() => {
    const widgets = Array.from(document.querySelectorAll(".widget")).filter(
      Boolean
    );
    widgets.forEach((w, idx) => {
      w.style.opacity = 0;
      w.style.transform = "translateY(20px)";
      w.style.transition = `opacity 0.5s ease ${idx * 0.05}s, transform 0.5s ease ${
        idx * 0.05
      }s`;
    });
    const onScroll = () => {
      widgets.forEach((w) => {
        if (w.getBoundingClientRect().top < window.innerHeight / 1.2) {
          w.style.opacity = 1;
          w.style.transform = "translateY(0)";
        }
      });
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goEdit = () => {
    if (typeof onGoEdit === "function") return onGoEdit();
    if (typeof setRoute === "function") return setRoute("editDashboard");
  };

  const toggleSection = (widgetId) => {
    setExpandedSections(prev => ({ ...prev, [widgetId]: !prev[widgetId] }));
  };
  const toggleAllSections = (expand) => {
    const next = {};
    tableWidgets.forEach(w => { next[w.id] = !!expand; });
    setExpandedSections(next);
  };
  // Mode plein ecran retire : sections repliables uniquement

  return (
    <>
      {/* Header with global search and user information */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="page-title">Tableau de bord</h1>
          <div className="global-controls">
            <input
              type="text"
              className="search-box"
              placeholder="Recherche globale..."
            />
            <div className="user-menu">
              <div className="user-avatar">
                {user?.email?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <span>Utilisateur</span>
            </div>
          </div>
        </div>
      </div>

      <div className="content-container">
            {/* KPI en grille (inchangé) */}
            {kpiWidgets.length > 0 && (
              <div className="kpi-grid">
                {kpiWidgets.map((w) => (
                  <Widget key={w.id} user={user} widget={w} />
                ))}
              </div>
            )}

            {/* Autres widgets non-table en grille (inchangé) */}
            {nonTableWidgets.length > 0 && (
              <div className="widgets-grid" style={{ marginTop: 10 }}>
                {nonTableWidgets.map((w) => (
                  <Widget key={w.id} user={user} widget={w} />
                ))}
              </div>
            )}

            {/* Contrôles globaux des sections table */}
            {tableWidgets.length > 0 && (
              <div className="sections-controls">
                <button className="btn btn-outline" onClick={() => toggleAllSections(true)}>
                  <i className="fas fa-expand" /> Tout développer
                </button>
                <button className="btn btn-outline" onClick={() => toggleAllSections(false)}>
                  <i className="fas fa-compress" /> Tout réduire
                </button>
              </div>
            )}

            {/* Sections repliables par table */}
            {tableWidgets.map((widget) => {
              const isOpen = !!expandedSections[widget.id];
              return (
                <div key={widget.id} className={`collapsible-section ${isOpen ? "open" : ""}`}>
                  <div
                    className="collapsible-header"
                    onClick={() => toggleSection(widget.id)}
                    role="button"
                    tabIndex={0}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <i className={`fas ${widget.type === "relation_table" ? "fa-users" : "fa-table"}`} />
                      <strong>{widget.title || "Tableau"}</strong>
                    </div>
                    <i className="fas fa-chevron-down collapsible-chevron" aria-hidden="true" />
                  </div>
                  <div className="collapsible-body">
                    <Widget user={user} widget={widget} onEditRecord={onEditRecord} />
                  </div>
                </div>
              );
            })}
      </div>

      <div className="quick-actions-bar">
        <button className="btn btn-success" onClick={onGoUpload}>
          <i className="fas fa-upload"></i>
          Uploader un modèle
        </button>
        <button className="btn btn-outline" onClick={goEdit}>
          <i className="fas fa-edit"></i>
          Configurer
        </button>
      </div>
    </>
  );
}
